import { motion } from "framer-motion";
import { CalendarClock, Mail } from "lucide-react";
import { INTERNATIONAL, wa, slug, scrollToId } from "../lib/site";
import { MaskLines, SectionEyebrow } from "./Reveal";
import { WhatsAppIcon } from "./WhatsAppIcon";

const Card = ({ d, i, onInquire }) => (
  <motion.article
    initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: i * 0.1 }}
    className={`group relative flex flex-col overflow-hidden rounded-[28px] bg-zinc-900 ${i % 2 ? "lg:mt-16" : ""}`}
    data-testid={`intl-card-${slug(d.name)}`}
  >
    <div className="relative aspect-[3/4] overflow-hidden">
      <img src={d.img} alt={d.name} loading="lazy"
        className="h-full w-full object-cover grayscale-[30%] transition-[transform,filter] duration-[1.2s] ease-[cubic-bezier(.22,1,.36,1)] group-hover:scale-110 group-hover:grayscale-0" />
      <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/10 to-transparent" />
      <span className="absolute left-4 top-4 rounded-full bg-go px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-ink">From Dec</span>
      <div className="absolute inset-x-5 bottom-4">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-300">{d.country}</p>
        <h3 className="font-display text-4xl font-extrabold tracking-tight text-white">{d.name}</h3>
      </div>
    </div>
    <div className="flex flex-col gap-2 p-5 pt-3">
      <a href={wa(`Hi, I want to inquire about December slots for the ${d.name} package.`)} target="_blank" rel="noopener noreferrer"
        data-testid={`intl-inquire-${slug(d.name)}`}
        className="inline-flex items-center justify-center gap-2 rounded-full bg-white py-3 text-sm font-bold text-ink transition-[background-color] duration-300 hover:bg-go">
        <WhatsAppIcon className="h-4 w-4" /> Inquire for December Slots
      </a>
      <button onClick={() => onInquire(d.name)} data-testid={`intl-email-${slug(d.name)}`}
        className="inline-flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold text-zinc-300 transition-colors hover:text-go">
        <Mail size={14} /> or send an email inquiry
      </button>
    </div>
  </motion.article>
);

export const International = ({ onSelect }) => {
  const inquire = (name) => { onSelect(name); scrollToId("contact"); };
  return (
    <section id="international" className="relative bg-ink py-24 sm:py-32 text-white" data-testid="international-section">
      <div className="mx-auto max-w-7xl px-5">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <SectionEyebrow index="03" label="International Trips" light />
            <h2 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[0.92] tracking-[-0.035em]">
              <MaskLines lines={["International", <span key="e">Escapes<span className="text-go">.</span></span>]} />
            </h2>
          </div>
          <motion.div initial={{ scale: 0.8, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }} viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 200, damping: 14 }}
            className="relative inline-flex items-center gap-3 self-start rounded-full bg-go px-5 py-3 text-ink lg:self-auto" data-testid="intl-launch-badge">
            <span className="absolute inset-0 rounded-full bg-go animate-ping opacity-20" />
            <CalendarClock size={20} />
            <span className="font-display text-lg font-extrabold">Launching December Onwards</span>
          </motion.div>
        </div>
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {INTERNATIONAL.map((d, i) => <Card key={d.name} d={d} i={i} onInquire={inquire} />)}
        </div>
      </div>
    </section>
  );
};
